/* global jQuery */
import Vue from 'vue'
import router from '@/router'

// /!\ Always append new values to not break existing search urls
const encodeMapping = ['position', 'pet_list', 'smoking', 'dietaries', 'religious', 'meals', 'pace_of_life', 'outings_at_week', 'activities_we', 'sports', 'arts', 'appearance', 'hobby', 'thinking_about', 'decorating', 'time_at_home', 'clean_and_tidy', 'cooking', 'bathing', 'tinkering', 'gardening', 'watching_tv', 'work', 'education', 'business_area', 'character', 'service_list', 'ecoloc']

export const namespaced = true

export const state = {
  homes: {
    loading: false,
    from: 0,
    to: 12,
    page: 0,
    pages: [],
    sort: 'price-asc',
    count: null,
    pageCount: 0,
    items: [],
    error: null,
  },
  profiles: {
    loading: false,
    from: 0,
    to: 12,
    page: 0,
    pages: [],
    sort: '5',
    count: null,
    pageCount: 0,
    items: [],
    error: null,
  },
  search: {
    search_id: null,
    city_searches: [],
    radius: '5',
    country_id: 6,
    bedroom_count: 1,
    per_room_budget: [0, 1500],
    private_bathroom: null,
    private_toilets: null,
    age: [0, 120],
    situation: '',
    situations: { single: true, couple: true, family: true, group: true },
    gender: '',
    children: '',
    pets: '',
    smoking: '',
    ecoloc: null,
    handicap_friendly: null,
    pay_by_service: null,
    has_home: null,
  },
  matchingData: [],
  encodedFilters: '',
}

const fromSearch = search => ({
  search_id: search.search_id,
  home_id: search.home_id,
  city_searches: search.city_searches,
  radius: parseInt(search.radius) ? parseInt(search.radius) : 5,
  bedroom_count: search.bedroom_count ? search.bedroom_count : 1,
  budget_per_room_min: search.per_room_budget[0],
  budget_per_room_max: search.per_room_budget[1],
  age_min: search.age[0],
  age_max: search.age[1],
  private_bathroom: search.private_bathroom,
  private_toilets: search.private_toilets,
  situation: search.situation ? parseInt(search.situation) : 0,
  situations: { ...search.situations },
  gender: search.gender ? parseInt(search.gender) : 0,
  children: search.children ? parseInt(search.children) : 0,
  pets: search.pets ? parseInt(search.pets) : 0,
  smoking: search.smoking ? parseInt(search.smoking) : 0,
  ecoloc: search.ecoloc,
  handicap_friendly: search.handicap_friendly,
  pay_by_service: search.pay_by_service,
  has_home: search.has_home ? parseInt(search.has_home) : 0,
})

export const actions = {

  loadHomes({ commit, state }, req) {
    commit('setHomesLoading', true)

    const search = fromSearch(state.search)

    if (search.search_id && (!search.city_searches || search.city_searches.length === 0)) {
      commit('setHomesResult', { items: [], count: 0, pageCount: 0, page: 1, pages: [] })
      commit('setHomesLoading', false)
      return state.search
    }

    let page = req.page !== undefined ? req.page : (state.homes.page || 0)
    const sort = req.sort !== undefined ? req.sort : state.homes.sort
    const url = '/api/match/user-match-homes-v4'
    return Vue.$http.post(url, search, { params: { from: page * 12, to: (page + 1) * 12, sort } }).then(
      res => {
        const items = res.data.Hits.map(home => ({
          ...home,
          tenant_images: (home.tenant_images || '').split(','),
          external: home.link && home.link.startsWith('http'),
          image: home.image_link && home.image_link.startsWith('http')
            ? {
              'background-image': `url(${home.image_link})`,
              'background-color': 'white',
            }
            : home.image_link
              ? {
                'background-image': `url(/image/${home.image_link}/450/0)`,
                'background-color': 'white',
              }
              : {
                'background-image': 'url(/static/images/icons/no-picture.svg)',
                'background-color': '#A7A9AC',
                'background-size': 'contain',
              },
        })) || []
        const count = res.data.Count
        const pageCount = Math.floor(res.data.Count / 12) + ((res.data.Count % 12 === 0) ? 0 : 1)
        if (page > pageCount) { page = pageCount - 1 }
        const pages = []
        for (let p = page - 4; p < page + 4 && p < pageCount && pages.length < 5; p++) {
          if (p >= 0) pages.push(p)
        }

        commit('setHomesResult', { items, count, pageCount, page, pages })

        const search = {
          search_id: res.data.Search.search_id,
          city_searches: res.data.Search.city_searches,
          radius: res.data.Search.radius.toString(),
          per_room_budget: [res.data.Search.budget_per_room_min, res.data.Search.budget_per_room_max],
          private_bathroom: res.data.Search.private_bathroom,
          private_toilets: res.data.Search.private_toilets,
          ecoloc: res.data.Search.ecoloc,
          pay_by_service: res.data.Search.pay_by_service,
        }
        commit('updateSearch', search)

        return search
      },
      err => {
        commit('setHomesError', { error: err.message })
        console.error(err)
      },
    ).finally(() => {
      commit('setHomesLoading', false)
    })
  },

  loadProfiles({ commit, state }, req) {
    commit('setProfilesLoading', true)

    const PAGE_SIZE = 12

    const search = fromSearch(state.search)
    if (search.search_id && (!search.city_searches || search.city_searches.length === 0)) {
      commit('setProfilesResult', { items: [], count: 0, pageCount: 0, page: 1, pages: [] })
      commit('setProfilesLoading', false)
      return state.search
    }

    let page = req.page !== undefined ? req.page : (state.profiles.page || 0)
    const sort = req.sort !== undefined ? req.sort : state.profiles.sort
    return Vue.$http.post('/api/match/user-match-users-v4?count=force', search, { params: { from: page * PAGE_SIZE, to: (page + 1) * PAGE_SIZE, sort } }).then(
      res => {
        const items = (res.data.Hits || []).map(match => ({
          ...match.SecondUser,
          Position: match.SecondUser.PositionComputed,
          GlobalScore: match.GlobalScore.Total,
          SearchScore: match.GlobalScore.Total / 2,
          Favoris: match.Favoris,
        }))
        const count = res.data.HitsCount
        const pageCount = Math.floor(res.data.HitsCount / PAGE_SIZE) + ((res.data.HitsCount % PAGE_SIZE === 0) ? 0 : 1)
        if (page >= pageCount) { page = pageCount - 1 }
        if (page < 0) { page = 0 }
        const pages = []
        for (let p = page - 2; p < page + 5 && p < pageCount && pages.length < 5; p++) {
          if (p >= 0) pages.push(p)
        }

        commit('setProfilesResult', { items, count, pageCount, page, pages })

        const search = {
          search_id: res.data.Search.search_id,
          city_searches: res.data.Search.city_searches,
          radius: res.data.Search.radius.toString(),
          per_room_budget: [res.data.Search.budget_per_room_min, res.data.Search.budget_per_room_max],
          age: [res.data.Search.age_min, res.data.Search.age_max],
          gender: res.data.Search.gender.toString(),
          situation: res.data.Search.situation.toString(),
          children: res.data.Search.children.toString(),
          pets: res.data.Search.pets.toString(),
          smoking: res.data.Search.smoking.toString(),
          ecoloc: res.data.Search.ecoloc,
          handicap_friendly: res.data.Search.handicap_friendly,
        }
        commit('updateSearch', search)

        return search
      },
      err => {
        commit('setProfilesError', { error: err.message })
        console.error(err)
      },
    ).finally(() => {
      commit('setProfilesLoading', false)
    })
  },

  loadProfilesForHome({ commit, state }, req) {
    commit('setProfilesLoading', true)

    const PAGE_SIZE = 12

    const search = fromSearch(state.search)
    let page = req.page !== undefined ? req.page : (state.profiles.page || 0)
    const sort = req.sort !== undefined ? req.sort : state.profiles.sort
    return Vue.$http.post(`/api/match/home-match-users-v4/${req.home_id}?count=force`, search, { params: { from: page * PAGE_SIZE, to: (page + 1) * PAGE_SIZE, sort } }).then(
      res => {
        const items = (res.data.Hits || [])
        const count = res.data.Count
        const pageCount = Math.floor(res.data.Count / PAGE_SIZE) + ((res.data.Count % PAGE_SIZE === 0) ? 0 : 1)
        if (page >= pageCount) { page = pageCount - 1 }
        if (page < 0) { page = 0 }
        const pages = []
        for (let p = page - 2; p < page + 5 && p < pageCount && pages.length < 5; p++) {
          if (p >= 0) pages.push(p)
        }

        commit('setProfilesResult', { items, count, pageCount, page, pages })
        return search
      },
      err => {
        if (err.response && err.response.status === 424 && err.response.data && err.response.data.error && err.response.data.error.startsWith('incomplete')) {
          commit('setProfilesError', 'Annonce incomplète')
          return search
        }
        commit('setProfilesError', err.response && err.response.data ? err.response.data.error : err.message)
        console.error(err)
      },
    ).finally(() => {
      commit('setProfilesLoading', false)
    })
  },
}

export const mutations = {
  setHomesLoading(state, loading) {
    state.homes.loading = loading
  },

  setHomesResult(state, homes) {
    state.homes.error = null
    state.homes.items = homes.items
    state.homes.count = homes.count
    state.homes.pageCount = homes.pageCount
    state.homes.page = homes.page
    state.homes.pages = homes.pages
  },

  setHomesError(state, error) {
    state.homes.error = error
    state.homes.items = []
  },

  setProfilesLoading(state, loading) {
    state.profiles.loading = loading
  },

  setProfilesResult(state, profiles) {
    state.profiles.error = null
    state.profiles.items = profiles.items
    state.profiles.count = profiles.count
    state.profiles.pageCount = profiles.pageCount
    state.profiles.page = profiles.page
    state.profiles.pages = profiles.pages
    state.matchingData = profiles && profiles.items ? profiles.items : []
  },

  setProfilesError(state, error) {
    state.profiles.error = error
    state.profiles.items = []
  },

  updateSearch(state, search) {
    Object.assign(state.search, search)
  },

  /** Change search filter */
  setSearchField(state, { prop, value }) {
    // Change state
    if (prop === 'sortby' || prop === 'activeFilter') {
      Vue.set(state, prop, value)
    } else {
      Vue.set(state.search, prop, value)
    }

    // Reset results
    state.matchingRangeFrom = 0
    state.matchingRangeTo = 15
    state.reachedEnd = false
    state.matchingData = []

    // Change search url query
    const id = state.encodedFilters = encodeFilters(state)
    const $route = router.currentRoute
    if ($route.query.search !== id) {
      router.replace({
        query: {
          ...$route.query,
          search: id,
        },
      })
      if ('localStorage' in window) {
        localStorage.setItem('last-search', id)
      }
    }
  },

  /** Add loaded results */
  addResults(state, data) {
    // User has no search
    if (data.missing_search) {
      return state.missing_search = true
    }

    if (data.reset) {
      state.matchingData = []
    }

    state.missing_search = false
    if (state.matchingRangeFrom === 0) {
      state.count = data.HitsCount
    }

    // Increment pagination
    const delta = state.matchingRangeTo - state.matchingRangeFrom
    state.matchingRangeFrom = state.matchingRangeTo
    state.matchingRangeTo += delta

    const hits = data.Hits
    if (hits && hits.length) {
      for (let i = 0, l = hits.length; i < l; i++) {
        // hits[i].SecondUser.ProfilePosition = hits[i].SecondUser.PositionComputed
        hits[i].SecondUser.Position = hits[i].SecondUser.PositionComputed
        hits[i].SecondUser.GlobalScore = hits[i].GlobalScore.Total
        hits[i].SecondUser.SearchScore = hits[i].GlobalScore.Total / 2
        hits[i].SecondUser.Favoris = hits[i].SecondUser.IsFavorite
        state.matchingData.push(hits[i].SecondUser)
      }
    } else {
      state.reachedEnd = true
    }
  },

  /** Change active filter pannel */
  toggleActive(state, index) {
    if (state.activeFilter === index) {
      state.activeFilter = 0
    } else {
      state.activeFilter = index
    }
  },

  /** Remove an user from the matching results */
  removeFromResults(state, index) {
    state.matchingData.splice(index, 1)
  },

  /** Reload the last search from the url or localStorage */
  reloadLastSearch(state, encoded_filters) {
    state.matchingRangeFrom = 0
    state.matchingRangeTo = 15
    state.reachedEnd = false
    state.matchingData = []

    // Reload last search
    const $route = router.currentRoute
    if (encoded_filters) {
      decodeFilters(state, encoded_filters)
      state.encodedFilters = encodeFilters(state)
    } else if ($route.query.search) {
      decodeFilters(state, $route.query.search)
      state.encodedFilters = encodeFilters(state)
    } else if ('localStorage' in window) {
      const last_search = localStorage.getItem('last-search')
      if (last_search) {
        decodeFilters(state, last_search)
        state.encodedFilters = encodeFilters(state)
      }
    }
  },
}

/** Encode filters to the smallest string possible */
function encodeFilters(state) {
  let filters = state.sortby + ';' + state.activeFilter
  for (let key in state.search) {
    const val = Array.isArray(state.search[key]) ? state.search[key].join(',') : ((state.search[key] * 1 || 0) + '')
    if (val && val !== '0') {
      if (key === 'has_home' && val === 1) continue

      const index = encodeMapping.indexOf(key)
      if (index >= 0) key = index
      filters += ';' + key + ':' + val
    }
  }
  if (filters === ';1') return undefined
  return btoa(filters).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_')
}

/** Decode encoded filters */
function decodeFilters(state, { filters, instance }) {
  if (!filters) return

  filters = filters.replace(/-/g, '+').replace(/_/g, '/')
  try {
    filters = atob(filters)
  } catch (err) {
    console.warn('Wrong search ignored: ', err)
    return
  }
  filters = filters.split(';')

  state.sortby = filters.shift()
  state.activeFilter = filters.shift()

  for (const filter of filters) {
    let [field, value] = filter.split(':')
    field = encodeMapping[field] || field
    if (Array.isArray(state.search[field])) {
      Vue.set(state.search, field, value.split(',').map(value => value * 1))
      const select2bug = {
        // Variable name: [id html, this.xxxx.configuration]
        pet_list: ['searchanimaux', 'pets'],
        sports: ['searchsports', 'sports'],
        arts: ['searcharts', 'arts'],
        thinking_about: ['search-thinking_about', 'thinking_about'],
        character: ['searchform-profil-character', 'character'],
        service_list: ['service_list', 'service_list'],
        outings_at_week: ['search-outings', 'outings_at_week'],
      }[field]
      if (select2bug) setTimeout(() => jQuery('#' + select2bug[0]).select2(instance[select2bug[1]].configuration), 100)
    } else {
      Vue.set(state.search, field, value * 1)
    }
  }
}

/**
 * Compress a guid by merging 4 by 4 characters
 * @param {string} guid
 * @returns {string}
 */
export const compressGuid = guid => {
  const from = '0123456789abcdef'
  const to = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
  return guid.toLowerCase().replace(/-/g, '').match(/.{1,4}/g).map(str => {
    let res = 0
    for (let i = 0, l = str.length; i < l; i++) {
      res += from.indexOf(str.charAt(i))
    }
    return to.charAt(res)
  }).join('')
}
